import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  BarChart3,
  FileText,
  CheckSquare,
  FileBarChart2,
  Settings,
  Sun,
  ChevronLeft,
  ChevronRight,
  LogOut,
  ScanLine,
} from 'lucide-react'
import { useUI } from '@/lib/store'
import { useAuth } from '@/hooks/useAuth'
import { getRoleInfo } from '@/lib/constants'

// =============================================================================
// Navigation items
// =============================================================================

interface NavItem {
  path:        string
  label:       string
  icon:        React.ComponentType<{ className?: string }>
  roles?:      string[]
  badge?:      number
}

const NAV_ITEMS: NavItem[] = [
  { path: '/',           label: 'Dashboard',     icon: LayoutDashboard },
  { path: '/layout',     label: 'Layout Planta',  icon: ScanLine },
  { path: '/production', label: 'Producción',     icon: BarChart3 },
  { path: '/documents',  label: 'Documentos',     icon: FileText },
  { path: '/validation', label: 'Validaciones',   icon: CheckSquare, roles: ['ADMIN', 'SUPERVISOR_COBRA'] },
  { path: '/reports',    label: 'Informes',       icon: FileBarChart2 },
  { path: '/admin',      label: 'Administración', icon: Settings, roles: ['ADMIN'] },
]

// =============================================================================
// Sidebar Component
// =============================================================================

export default function Sidebar() {
  const { sidebarOpen, toggleSidebar } = useUI()
  const { user, logout }               = useAuth()
  const location                       = useLocation()

  const roleInfo = user ? getRoleInfo(user.role) : null
  const userInitials = user?.full_name
    ?.split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() ?? 'U'

  const visibleNavItems = NAV_ITEMS.filter((item) => {
    if (!item.roles) return true
    return user && item.roles.includes(user.role)
  })

  return (
    <aside
      className={`
        fixed inset-y-0 left-0 z-30 flex flex-col bg-sidebar text-white
        transition-all duration-300 ease-in-out
        ${sidebarOpen ? 'w-64' : 'w-16'}
      `}
    >
      {/* Header */}
      <div className="flex items-center h-16 px-4 border-b border-sidebar-border shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-yellow-400 shrink-0">
            <Sun className="w-5 h-5 text-yellow-900" />
          </div>
          {sidebarOpen && (
            <div className="min-w-0">
              <p className="text-sm font-bold text-white truncate leading-tight">PSFV Navabuena</p>
              <p className="text-xs text-slate-400 truncate leading-tight">EPC Tracker</p>
            </div>
          )}
        </div>

        {/* Toggle button */}
        <button
          onClick={toggleSidebar}
          className="ml-auto p-1 rounded-lg text-slate-400 hover:text-white hover:bg-sidebar-hover transition-colors shrink-0"
          title={sidebarOpen ? 'Contraer menú' : 'Expandir menú'}
        >
          {sidebarOpen ? (
            <ChevronLeft className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto sidebar-scroll py-4 px-2">
        <ul className="space-y-1">
          {visibleNavItems.map((item) => {
            const Icon      = item.icon
            const isActive  = item.path === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.path)

            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  title={!sidebarOpen ? item.label : undefined}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                    transition-colors duration-150 group relative
                    ${isActive
                      ? 'bg-sidebar-active text-white'
                      : 'text-slate-300 hover:bg-sidebar-hover hover:text-white'
                    }
                    ${!sidebarOpen ? 'justify-center' : ''}
                  `}
                >
                  <Icon
                    className={`w-5 h-5 shrink-0 ${
                      isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'
                    }`}
                  />
                  {sidebarOpen && (
                    <span className="truncate">{item.label}</span>
                  )}
                  {sidebarOpen && item.badge !== undefined && item.badge > 0 && (
                    <span className="ml-auto text-xs bg-red-500 text-white rounded-full px-1.5 py-0.5 leading-none min-w-[1.25rem] text-center">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                  {/* Tooltip for collapsed state */}
                  {!sidebarOpen && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap
                                    opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                      {item.label}
                    </div>
                  )}
                </NavLink>
              </li>
            )
          })}
        </ul>
      </nav>

      {/* User section */}
      <div className="shrink-0 border-t border-sidebar-border p-3">
        {user ? (
          <div className={`flex items-center gap-3 ${!sidebarOpen ? 'justify-center' : ''}`}>
            {/* Avatar */}
            <div className="relative shrink-0">
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.full_name ?? 'Usuario'}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-brand-600 flex items-center justify-center text-xs font-bold text-white">
                  {userInitials}
                </div>
              )}
              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-400 border-2 border-sidebar" />
            </div>

            {sidebarOpen && (
              <>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate leading-tight">
                    {user.full_name ?? user.id.slice(0, 8)}
                  </p>
                  {roleInfo && (
                    <span
                      className="text-xs font-medium px-1.5 py-0.5 rounded-full inline-block mt-0.5"
                      style={{
                        backgroundColor: `${roleInfo.color}25`,
                        color: roleInfo.color,
                      }}
                    >
                      {roleInfo.shortLabel}
                    </span>
                  )}
                </div>

                <button
                  onClick={logout}
                  title="Cerrar sesión"
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-sidebar-hover transition-colors shrink-0"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            )}

            {!sidebarOpen && (
              <button
                onClick={logout}
                title="Cerrar sesión"
                className="absolute bottom-16 left-0 right-0 mx-auto w-8 flex items-center justify-center p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-sidebar-hover transition-colors"
              >
                {/* Hidden in collapsed — only show avatar */}
              </button>
            )}
          </div>
        ) : (
          <div className="h-8" />
        )}
      </div>
    </aside>
  )
}
